import type { IconType, ThemeVariant } from '@nimling/components-nimtech'

export type SourceKind = 'otlp' | 'prometheus_write' | 'prometheus_scrape' | 'loki' | 'elastic' | 'syslog' | 'file_tail' | 'api_call' | 'openapi' | 'container_app' | 'azure_kql' | 'query' | 'mql' | 'lql' | 'traces'
export type SourceState = 'draft' | 'active' | 'erroneous' | 'unauthorized' | 'disabled'

export const SourceKinds = {
  OTLP: 'otlp',
  PrometheusWrite: 'prometheus_write',
  PrometheusScrape: 'prometheus_scrape',
  Loki: 'loki',
  Elastic: 'elastic',
  Syslog: 'syslog',
  FileTail: 'file_tail',
  APICall: 'api_call',
  OpenAPI: 'openapi',
  ContainerApp: 'container_app',
  AzureKQL: 'azure_kql',
  Query: 'query',
  MQL: 'mql',
  LQL: 'lql',
  Traces: 'traces'
} as const

export const SourceKindLabels: Record<SourceKind, string> = {
  otlp: 'OTLP sink',
  prometheus_write: 'Prometheus remote write',
  prometheus_scrape: 'Prometheus scrape',
  loki: 'Loki push',
  elastic: 'Elasticsearch bulk',
  syslog: 'Syslog',
  file_tail: 'File tail',
  api_call: 'Periodic HTTP GET',
  openapi: 'API from an OpenAPI spec',
  container_app: 'Azure Container Apps logs',
  azure_kql: 'Azure Log Analytics query',
  query: 'Query',
  mql: 'Metrics query (mql)',
  lql: 'Logs query (lql)',
  traces: 'Trace search'
}

export const SourceKindIcons: Record<SourceKind, IconType> = {
  otlp: 'opentelemetry',
  prometheus_write: 'prometheus',
  prometheus_scrape: 'prometheus',
  loki: 'loki',
  elastic: 'elastic',
  syslog: 'syslog',
  file_tail: 'file',
  api_call: 'globe',
  openapi: 'sdk',
  container_app: 'azure',
  azure_kql: 'azure',
  query: 'database',
  mql: 'chart-line',
  lql: 'filter',
  traces: 'activity'
}

export const SourceSinkKinds: SourceKind[] = ['otlp', 'prometheus_write', 'loki', 'elastic', 'syslog']
export const SourcePolledKinds: SourceKind[] = ['api_call', 'container_app', 'azure_kql']

export interface SourceConnection {
  poll_ms: number
  max_entries: number
  trailing_window: string
}

export type ContainerAppTailMode = 'lines' | 'time'

export interface ContainerAppConfig {
  subscription: string
  tenant_id?: string | null
  resource_group: string
  container_app: string
  workspace_id?: string | null
  tail_mode?: ContainerAppTailMode
  tail_value?: number
}

export interface AzureKQLConfig {
  subscription: string
  tenant_id?: string | null
  workspace_id: string
  default_query: string
}

export interface APICallConfig {
  base_url: string
  endpoint_method: string
  endpoint_path: string
  endpoint_query?: Record<string, unknown> | null
  endpoint_body?: unknown | null
  endpoint_headers?: Record<string, string> | null
  response_path?: string | null
}

export type OTLPSignal = 'logs' | 'metrics' | 'traces'

export const OTLPSignals: OTLPSignal[] = ['logs', 'metrics', 'traces']

export interface OTLPConfig {
  signal: OTLPSignal
  service?: string
  environment?: string
  retention_days?: number
}

export type QueryLanguage = 'sql' | 'tengo'

export interface QueryCode {
  language: QueryLanguage
  code: string
  step?: string
}

export interface QueryReferenceFunction {
  token: string
  meta: string
  group: string
  description: string
}

export interface QueryReferenceTable {
  name: string
  columns: string[]
}

export interface QueryReference {
  functions: QueryReferenceFunction[]
  tables: QueryReferenceTable[]
}

export interface MQLConfig {
  expr: string
  step?: string
}

export interface PrometheusScrapeConfig {
  url: string
  interval?: string
  service?: string
  environment?: string
  labels?: Record<string, string>
}

export interface LQLConfig {
  pipeline: string
  step?: string
}

export type TraceStatus = 'unset' | 'ok' | 'error'

export const TraceStatuses: TraceStatus[] = ['unset', 'ok', 'error']

export interface TracesConfig {
  service?: string
  operation?: string
  min_duration_ms?: number | null
  max_duration_ms?: number | null
  status?: TraceStatus | ''
  attributes?: Record<string, string>
}

export type SourceConfig = ContainerAppConfig | AzureKQLConfig | APICallConfig | OpenAPIConfig | OTLPConfig | QueryCode | MQLConfig | PrometheusScrapeConfig | LQLConfig | TracesConfig | Record<string, unknown>

export type OpenAPIDelivery = 'pull' | 'webhook'

export interface OpenAPIEndpoint {
  name: string
  params?: Record<string, string>
  time?: string
}

export interface OpenAPIConfig {
  delivery: OpenAPIDelivery
  base_url?: string
  endpoints: OpenAPIEndpoint[]
}

export interface SchemaField {
  path: string
  type: string
}

export interface SpecEndpoint {
  name: string
  method: string
  path: string
  summary?: string
  kind: 'request' | 'webhook' | 'stream'
  path_params: string[]
  query_params: string[]
  item_path: string
  schema: Record<string, unknown>
  fields: SchemaField[]
}

export interface SourceSpec {
  title: string
  version: string
  hash: string
  url?: string
  servers: string[]
  auth: { name: string; type: string; scheme: string; in: string; key: string }[]
  endpoints: SpecEndpoint[]
}

export type AuthType = 'none' | 'bearer' | 'apiKey' | 'basic' | 'oauth2_client_credentials'

export interface SourceAuth {
  type: AuthType
  header_key?: string
  query_key?: string
  username?: string
  token_url?: string
  client_id?: string
  scope?: string
}

export type SourceSecretKey = 'token' | 'password' | 'client_secret' | 'signing_secret'

export type MappingSignal = 'logs' | 'metrics' | 'traces'

export interface SourceMapping {
  signal?: MappingSignal
  fields?: Record<string, string>
  attributes?: Record<string, string>
  schema?: Record<string, unknown>
}

export const SourceMappingFields: Record<MappingSignal, string[]> = {
  logs: ['time', 'body', 'severity_text', 'service', 'environment', 'trace_id', 'span_id'],
  metrics: ['time', 'name', 'value', 'service', 'environment'],
  traces: ['time', 'end_time', 'duration_ms', 'name', 'trace_id', 'span_id', 'parent_span_id', 'kind', 'status_code', 'service', 'environment']
}

export interface SourceIngest {
  endpoints: { protocol: string; url: string }[]
  headers: { name: string; value: string }[]
  auth: string
}

export interface LogColumn {
  key: string
  label?: string
  width?: string
  align?: string
  format?: 'timestamp' | 'level' | 'json' | 'block'
  className?: string
}

export interface Source {
  id: string
  name: string
  owner_id?: string
  description?: string | null
  kind: SourceKind
  parent_id?: string | null
  state: SourceState
  color: string
  connection: SourceConnection
  config: SourceConfig
  columns: LogColumn[]
  auth?: SourceAuth | null
  secret_keys?: SourceSecretKey[]
  mapping?: SourceMapping
  ingest?: SourceIngest | null
  api_spec?: unknown | null
  created_at: string
  updated_at: string
}

export const otlpSignalOf = (source: Source | null | undefined): OTLPSignal | null => {
  if (!source || source.kind !== 'otlp') return null
  const signal = (source.config as Partial<OTLPConfig>)?.signal
  return signal === 'logs' || signal === 'metrics' || signal === 'traces' ? signal : null
}

export const lqlAggregates = (pipeline: string): boolean => /\|\s*(stats|stats_by_time|uniq)\b/i.test(pipeline)

export const logRowSource = (source: Source | null | undefined): boolean => {
  if (!source) return false
  if (source.kind === 'lql') return !lqlAggregates(String((source.config as Partial<LQLConfig>)?.pipeline ?? ''))
  return source.kind === 'otlp' && (otlpSignalOf(source) ?? 'logs') === 'logs'
}

export type PageGridMode = 'dynamic' | 'fixed' | 'single'

export type PageGap = 'none' | 'tight' | 'roomy'

export const PageGapRem: Record<PageGap, number> = { none: 0, tight: 0.25, roomy: 0.75 }

export interface PageLayout {
  mode: PageGridMode
  columns: number
  rows: number
  gap: PageGap
  scroll_x: boolean
  bare?: boolean
}

export const PageGridModeLabels: Record<PageGridMode, string> = {
  dynamic: 'Dynamic, the page scrolls down and grows with its placements; views mount as they scroll into sight',
  fixed: 'Fixed, a set number of columns and rows fits the screen without scrolling',
  single: 'Single, one card fills the page'
}

export interface Page {
  id: string
  name: string
  parent_id?: string | null
  description?: string | null
  icon?: string | null
  is_fullscreen: boolean
  layout: PageLayout
  variables: PageVariable[]
  style?: string | null
  owner_id?: string
  created_at: string
  updated_at: string
}

export type ViewKind = 'graph' | 'log' | 'creative' | 'trace'
export type ViewState = 'draft' | 'erroneous' | 'active'

export const ViewKinds: ViewKind[] = ['graph', 'log', 'creative', 'trace']

export type ViewSignal = 'logs' | 'metrics' | 'traces' | 'records'

export const ViewSignals: ViewSignal[] = ['logs', 'metrics', 'traces', 'records']

export const ViewSignalLabels: Record<ViewSignal, string> = {
  logs: 'Logs, the log records in the store',
  metrics: 'Metrics, the metric samples and their rollups',
  traces: 'Traces, the spans of every request',
  records: 'Records, polled JSON the store does not hold yet'
}

export interface View {
  id: string
  name: string
  description?: string | null
  parent_id?: string | null
  state: ViewState
  signal: ViewSignal
  data?: string | null
  component?: string | null
  style?: string | null
  snapshot_at?: string | null
  owner_id?: string
  created_at: string
  updated_at: string
}

export interface Notification {
  id: string
  user_id: string
  title: string
  description?: string
  type: string
  link?: string
  object_id?: string
  object_type?: string
  created_by?: string
  owner_id?: string
  read_at?: string
  closed_at?: string
  created_at: string
}

export interface ViewLayout {
  x: number
  y: number
  w: number
  h: number
}

export type PageViewStatus = 'draft' | 'active' | 'erroneous'

export interface PageView {
  id: string
  page_id: string
  view_id: string
  layout: ViewLayout
  columns?: LogColumn[] | null
  config?: Record<string, unknown> | null
  style?: string | null
  status: PageViewStatus
  created_at: string
  updated_at: string
}

export interface KQLColumn {
  name: string
  type: string
}

export interface KQLResponse {
  source_id: string
  columns: KQLColumn[]
  rows: unknown[][]
}

export interface LogRecord {
  id: string
  time: string
  service: string
  environment: string
  severity_number: number
  severity_text: string
  body: string
  trace_id: string
  span_id: string
  attributes: Record<string, unknown>
  resource: Record<string, unknown>
  source_id?: string
}

export interface Span {
  time: string
  end_time: string
  service: string
  environment: string
  trace_id: string
  span_id: string
  parent_span_id: string
  name: string
  kind: string
  status_code: string
  status_message: string
  duration_ms: number
  attributes: Record<string, unknown>
  resource: Record<string, unknown>
  depth: number
}

export interface TraceResponse {
  trace_id: string
  spans: Span[]
}

export interface RangeResponse {
  mode: string
  from: string
  to: string
  step: string
  rows: Record<string, unknown>[]
  latency?: TraceLatency
}

export interface LogSearchResponse {
  q: string
  from: string
  to: string
  entries: LogRecord[]
}

export interface SearchHit {
  id: string
  kind: 'view' | 'page'
  name: string
  description?: string
  rank: number
}

export interface LogsQueryResponse {
  q: string
  from: string
  to: string
  step: string
  aggregates: boolean
  rows: Record<string, unknown>[]
}

export interface LogField {
  name: string
  count: number
}

export interface LogFieldsResponse {
  from: string
  to: string
  columns: string[]
  fields: LogField[]
}

export interface LogFieldValue {
  value: string
  count: number
}

export interface LogValuesResponse {
  field: string
  from: string
  to: string
  values: LogFieldValue[]
}

export interface MetricPoint {
  id: string
  bucket: string
  series: string
  labels: Record<string, unknown>
  value: number
}

export interface MetricsQueryResponse {
  expr: string
  from: string
  to: string
  step: string
  rows: MetricPoint[]
}

export interface MetricNamesResponse {
  names: string[]
}

export interface MetricLabelsResponse {
  name: string
  labels: Record<string, string[]>
}

export type RangeMode = 'live' | 'relative' | 'absolute'

export type RangeUnit = 'minute' | 'hour' | 'day' | 'week'

export interface TimeRange {
  from: string
  to: string
  step: string
}

export type AlertComparison = 'gt' | 'gte' | 'lt' | 'lte' | 'eq'
export type AlertSeverity = 'info' | 'warning' | 'critical'

export const AlertComparisons: AlertComparison[] = ['gt', 'gte', 'lt', 'lte', 'eq']
export const AlertSeverities: AlertSeverity[] = ['info', 'warning', 'critical']

export interface AlertRule {
  id: string
  source_id: string
  name: string
  query?: QueryCode | null
  comparison: AlertComparison
  threshold: number
  interval_seconds: number
  severity: AlertSeverity
  enabled: boolean
  action?: string | null
  owner_id?: string
  created_at: string
  updated_at: string
}

export interface AlertState {
  rule_id: string
  firing: boolean
  since?: string | null
  last_value?: number | null
  last_error?: string | null
  evaluated_at?: string | null
  updated_at: string
}

export interface AILog {
  id: string
  timestamp: string
  level: string
  message: string
  app_id: string
  replica?: string
  container?: string
}

export interface AISummary {
  headline: string
  severity: 'ok' | 'notice' | 'warning' | 'error'
  issues: { title: string; level: 'error' | 'warn' | 'info'; count: number; sample: string }[]
  metrics: { total_lines: number; errors: number; warnings: number }
  next_steps: string
}

export interface AISummaryResponse {
  source_id: string
  model: string
  body: AISummary
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatResponse {
  reply: string
  tool_results?: Array<Record<string, unknown>>
}

export interface SSEEvent {
  view_id: string
  type: string
  data: unknown
}

export interface TraceSummary extends Span {
  id: string
  trace_duration_ms: number
  span_count: number
  error_count: number
  services: string[]
}

export interface TraceSearchResponse {
  from: string
  to: string
  traces: TraceSummary[]
}

export interface LatencyPoint {
  bucket: string
  count: number
  p50: number
  p90: number
  p99: number
  max: number
}

export interface LatencyBin {
  from: number
  to: number
  count: number
}

export interface TraceLatency {
  points: LatencyPoint[]
  histogram: LatencyBin[]
}

export interface TraceLatencyResponse extends TraceLatency {
  from: string
  to: string
  step: string
  buckets: number
}

export interface TraceOperation {
  service: string
  operation: string
  count: number
  errors: number
  error_rate: number
  p99: number
}

export interface TraceService {
  service: string
  count: number
  errors: number
  error_rate: number
  p99: number
}

export interface TraceEdge {
  source: string
  target: string
  count: number
  errors: number
  error_rate: number
  p99: number
}

export interface TraceOperationsResponse {
  from: string
  to: string
  operations: TraceOperation[]
}

export interface TraceServicesResponse {
  from: string
  to: string
  services: TraceService[]
}

export interface TraceGraphResponse {
  from: string
  to: string
  nodes: TraceService[]
  edges: TraceEdge[]
}

export interface TraceSearchFilter {
  service?: string
  operation?: string
  min_duration?: number | null
  max_duration?: number | null
  status?: TraceStatus | ''
  attributes?: Record<string, string>
  take?: number
}

export type VariableKind = 'static' | 'query' | 'interval'

export const VariableKinds: VariableKind[] = ['static', 'query', 'interval']

export interface PageVariable {
  name: string
  label?: string
  kind: VariableKind
  options?: string[]
  query?: { source_id: string; column: string } | null
  default?: string | string[] | null
  multi?: boolean
}

export type VariableValues = Record<string, string[]>

export type TransformKind = 'filter' | 'rename' | 'compute' | 'sort' | 'limit' | 'group' | 'join'

export const TransformKinds: TransformKind[] = ['filter', 'rename', 'compute', 'sort', 'limit', 'group', 'join']

export const TransformArgHints: Record<TransformKind, string> = {
  filter: '{"field":"service","op":"eq","value":"api"}  ops: eq ne gt gte lt lte contains regex exists',
  rename: '{"from":"avg","to":"value"}',
  compute: '{"name":"ratio","expression":"errors / count * 100"}',
  sort: '{"field":"value","desc":true}',
  limit: '{"n":10}',
  group: '{"by":"service","aggregate":"sum","field":"value"}  aggregates: count sum avg min max',
  join: '{"with":"<placement id>","on":"service"}'
}

export interface Transform {
  kind: TransformKind
  args: Record<string, unknown>
}

export interface PageExport {
  version: number
  page: Record<string, unknown> & { slug: string; name: string }
  sources: Record<string, unknown>[]
  views: Record<string, unknown>[]
  placements: Record<string, unknown>[]
}

export interface ImportResponse {
  page: Page
  sources: number
  views: number
  placements: number
}

export interface SidebarGroup {
  id: string
  label: string
  icon: string
  items: string[]
}

export interface MobileTab {
  key: string
  label: string
  icon: IconType
  href?: string
}

export interface SettingValue {
  sidebar?: { order?: string[]; groups?: SidebarGroup[]; split?: number; split_collapsed?: { top?: boolean; bottom?: boolean } }
  mobile_tabs?: string[]
  appearance?: { theme?: ThemeVariant }
  public_claim_id?: string
  guest_stream?: boolean
  public_source_id?: string
  home?: boolean
  start?: 'welcome' | 'page'
  runtime_max_seconds?: number
  defaults?: SettingDefaults
  [key: string]: unknown
}

export interface Setting {
  object_type: string
  object_id: string
  value: SettingValue
}

export interface AlertWebhook {
  id: string
  rule_id: string
  url: string
  secret?: string
  active: boolean
  created_at: string
  updated_at: string
}

export interface AlertDelivery {
  id: string
  webhook_id: string
  rule_id: string
  payload: Record<string, unknown>
  fire_at: string
  processed_at?: string
  attempts: number
  status_code?: number
  failure_reason?: string
  created_at: string
}

export interface SamnaUser {
  id: string
  user_id: string
  first_name?: string
  last_name?: string
  email: string
  user_image?: string | null
}

export interface ClaimObject {
  id?: string
  claim_id?: string
  object_id: string
  object_type: string
  access?: number
  inherits?: boolean
  access_string?: string
  reason?: string | null
}

export interface UserClaim {
  id?: string
  claim_id: string
  user_id: string
}

export interface Claim {
  id: string
  name: string
  description?: string | null
  access: number
  inherits: boolean
  level?: number
  type: string
  owner_id: string
  guest_mode?: GuestMode
  claim_objects?: ClaimObject[]
  claim_users?: UserClaim[]
}

export type GuestMode = 'open' | 'code' | 'off'

export interface AccessCode {
  id: string
  kind: 'guest' | 'invite'
  claim_id: string
  starts_at?: string
  ends_at?: string
  count: number
  used_count: number
  revoked_at?: string
  created_at: string
  data?: { email?: string }
}

export interface AccessCodeBody {
  kind: 'guest' | 'invite'
  path: string
  ends_at?: string
  count: number
  email?: string
  message?: string
}

export interface AccessCodeCreated extends AccessCode {
  value: string
  url: string
}

export interface Annotation {
  id: string
  page_view_id: string
  time: string
  end_time?: string | null
  title: string
  text?: string | null
  tags: string[]
  owner_id?: string
  created_at: string
  updated_at: string
}

export interface ViewTemplate {
  id: string
  name: string
  description?: string | null
  state: ViewState
  signal: ViewSignal
  data?: string | null
  component?: string | null
  style?: string | null
}

export interface MeClaim extends Claim {
  access_slice?: string[]
  access_string?: string
  root_id?: string
}

export interface Organization {
  id: string
  name: string
  description?: string | null
  type?: string
  owner_id: string
  root_id: string
  level?: number
}

export type RetentionSignal = OTLPSignal | 'records' | 'metrics_1h' | 'alert_delivery' | 'ai_log'

export interface Retention {
  id: string
  signal: RetentionSignal
  class: string
  days: number
  owner_id?: string
  created_at: string
  updated_at: string
}

export interface Storage {
  series_cap: number
  log_hot_hours: number
  log_stream_labels: string[]
}

export interface RoutingMatch {
  service?: string
  environment?: string
  severity?: string
  attribute?: Record<string, string>
}

export interface RoutingRule {
  id: string
  signal: OTLPSignal
  name: string
  match: RoutingMatch
  class: string
  priority: number
  enabled: boolean
  owner_id?: string
  created_at: string
  updated_at: string
}

export type SamplingKind = 'always' | 'error' | 'latency' | 'probabilistic' | 'rate_limit'

export interface SamplingSpec {
  kind: SamplingKind
  attributes?: Record<string, string>
  latency_ms?: number
  percent?: number
  per_second?: number
}

export interface SamplingRule {
  id: string
  name: string
  enabled: boolean
  priority: number
  rule: SamplingSpec
  owner_id?: string
  created_at: string
  updated_at: string
}

export interface SettingDefaults {
  source?: Partial<SourceConnection>
  page?: Partial<PageLayout>
  placement?: { w?: number; h?: number; annotations?: boolean }
  alert?: { interval_seconds?: number; severity?: AlertSeverity }
}
